import { Hint } from "@/HOC";
import { Autocomplete, createFilterOptions, Popper, TextField } from "@mui/material";
import NoOptionText from "../NoOptionText/NoOptionText";
import { useTranslate } from "@/hooks";
import { colorPrimary } from "@/constants/colors";
import { IAutocompleteOption, ITextfieldWithAutocompleteSmall } from "./TextfieldWithAutocompleteSmall.props";
import { FC, memo, useMemo } from "react";
import RenderOption from "../RenderOption/RenderOption";

const TextfieldWithAutocompleteSmall: FC<ITextfieldWithAutocompleteSmall> = ({
	autocompleteOptions,
	freeSolo = false,
	value,
	label,
	setValues,
	values,
	onChange,
	name,
	inputRef,
	virtualize = false,
	valueFrom = "label",
	shrink,
	isDisabled = false,
	onBlur = () => {},
	lineThrough = false,
	isError = false,
	...props
}) => {
	const translate = useTranslate();
	const normalizedOptions = useMemo<IAutocompleteOption[]>(() => {
		if (!autocompleteOptions) return [];
		return autocompleteOptions.map((opt) => ({ value: opt.value, label: opt.label }));
	}, [autocompleteOptions]);

	// const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
	// 	onChange(e);
	// };

	// Новый режим (options с id и label). Изменение значения
	const handleChange = (_: React.SyntheticEvent, newValue: IAutocompleteOption | string | null) => {
		if (!setValues) return;

		let idValue: string | number = "";
		if (newValue === null) {
			idValue = "";
		} else if (typeof newValue === "string") {
			idValue = newValue;
		} else {
			idValue = newValue.value;
		}

		setValues({ ...values, [name]: idValue });
	};

	const filterOptions = createFilterOptions<IAutocompleteOption>({
		stringify: (option) => `${option.label} ${option.value}`, // ищем и по label, и по value
	});

	return (
		<Hint hint={translate("hint_conditionTitle")} position="top">
			<Autocomplete<any, false, boolean, boolean>
				{...props}
				freeSolo={freeSolo}
				autoHighlight
				options={normalizedOptions}
				value={normalizedOptions.find((opt) => opt.value === value) || value || null}
				clearIcon={null}
				onChange={handleChange}
				noOptionsText={<NoOptionText />}
				disabled={isDisabled}
				filterOptions={filterOptions}
				renderInput={(params) => (
					<TextField
						{...params}
						// error={selectId === ""}
						placeholder={isDisabled ? "" : translate("select_name")}
						// onBlur={() => onBlur()}
						// TODO. Поставил пока blur для freesolo, иначе значения не записываются
						onBlur={(e) => {
							if (freeSolo) {
								if (!setValues) return;
								setValues({ ...values, [name]: e.target.value });
							}
						}}
						title={normalizedOptions.find((opt) => opt.value === value)?.label || value}
						variant={"standard"}
						InputLabelProps={{
							style: {
								fontSize: 10,
							},
						}}
						sx={{
							'.MuiInputLabel-root[data-shrink="false"]': { top: -8 },
							"& .MuiAutocomplete-inputRoot": {
								paddingRight: "4px !important", // или любое другое значение
							},
							backgroundColor: "#efefef",
							boxShadow: isError ? "0 0 5px rgba(255, 0, 0, 0.5)" : "none",
							borderRadius: "4px ",
							"& .MuiInput-underline:before": {
								borderBottom: "none",
							},
							"& .MuiInput-underline:after": {
								borderBottom: "none",
							},
							"& .MuiInput-underline:hover:not(.Mui-disabled):before": {
								borderBottom: "none",
							},
						}}
					/>
				)}
				sx={{
					".MuiInputBase-root": {
						height: 23,
						fontSize: 10,
						color: colorPrimary,
						textDecoration: lineThrough ? "line-through" : undefined,
					},
					"& .MuiAutocomplete-popupIndicator": {
						mr: "2px",
					},
					"& .MuiSvgIcon-root": {
						width: "18px !important",
						height: "18px !important",
					},
					".MuiInputBase-input": { marginTop: -1, p: "0 0 0 4px !important" },
					"& .MuiAutocomplete-clearIndicator": {
						pr: "0px",
						"& svg": {
							width: 14, // Уменьшаем размер иконки
							height: 14,
							fontSize: 14,
						},
					},
				}}
				getOptionLabel={(option) => (typeof option === "string" ? option : valueFrom === "label" ? option.label : String(option.value))}
				PopperComponent={(props) => (
					<Popper
						{...props}
						style={{
							...props.style,
							width: "fit-content", // или "max-content"
							minWidth: "200px", // минимальная ширина
							maxWidth: "300px",
						}}
						placement="bottom-start"
					/>
				)}
				renderOption={(props, option) => {
					return (
						<RenderOption
							{...props}
							key={option.value}
							style={{
								fontSize: 10,
								paddingLeft: "8px",
							}}
						>
							{option.label}
						</RenderOption>
					);
				}}
			/>
		</Hint>
	);
};

export default memo(TextfieldWithAutocompleteSmall, (prev, next) => {
	const isValueSame = prev.value === next.value;
	const isValuesSame = prev.values === next.values; // TODO еще одна зависимость, из-за которой при смене одного поля сбрасывалось другое, т.к. это поле не было в курсе о текущем состоянии другого поля
	const isDisabledSame = prev.isDisabled === next.isDisabled;
	const isOptionsSame = prev.autocompleteOptions === next.autocompleteOptions; // ссылка стабильна через useMemo
	const isNameSame = prev.name === next.name;
	const isLineThroughSame = prev.lineThrough === next.lineThrough;
	const isErrorSame = prev.isError === next.isError;

	return isValueSame && isValuesSame && isDisabledSame && isOptionsSame && isNameSame && isLineThroughSame && isErrorSame;
});
